import React, { FC } from 'react';
import { GridCol, GridRow } from '@naf/grid';
import { TextVariant } from '@naf/text';
import { Label, Message } from '@naf/input';
import { StepWizardChildProps } from 'react-step-wizard';
import { FormProvider } from 'react-hook-form';
import type { ProfileForm } from '@dtp/membership-service-types';
import { WizardNavigation } from '@pages/BecomeAMember/components/WizardNavigation';
import { BirthDateDay } from '@components/Form/fieldInputs/BirthDateDay';
import { BirthDateMonth } from '@components/Form/fieldInputs/BirthDateMonth';
import { BirthDateYear } from '@components/Form/fieldInputs/BirthDateYear';
import type { CompareMembershipBlockType } from '@appTypes/CompareMembershipBlockType';
import { Address } from '@components/Address';
import { buyIdpPath } from '@universal/apps/Themed/ThemedApp.utils';
import S from './styles';
import { FirstName } from './fieldInputs/FirstName';
import { LastName } from './fieldInputs/LastName';
import { Email } from './fieldInputs/Email';
import { ZipCode } from './fieldInputs/Zip';
import { City } from './fieldInputs/City';
import { BirthCity } from './fieldInputs/BirthCity';
import { BirthCountry } from './fieldInputs/BirthCountry';
import { Summary } from '../../components/Summary/Summary';
import { AlreadyAMemberButton } from '../../components/AlreadyAMemberButton/AlreadyAMemberButton';
import { PhoneNumber } from './fieldInputs/PhoneNumber';
import { parsePhoneNumber } from '../../../../lib/phoneValidation';
import { MembershipBundle } from './MembershipBundle/MembershipBundle';
import { useHolderFormLogic } from './useHolderFormLogic';

// Define the form - receives props from wizard
export const Holder: FC<
  Partial<
    StepWizardChildProps & {
      profile?: ProfileForm;
      membershipProductData?: CompareMembershipBlockType;
    }
  >
> = ({ totalSteps, currentStep, nextStep, previousStep, goToStep, profile, membershipProductData }) => {
  const {
    form,
    errors,
    isAuthenticated,
    idpCustomerForm,
    internationalDrivingPermitForm,
    onSubmit,
    onGoBack,
    isValid,
  } = useHolderFormLogic({ profile, goToStep });
  const { control, handleSubmit, clearErrors, setValue } = form;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <GridRow>
        <GridCol s="12" m="12" l="8" xl="8">
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors.eMail?.type === 'server' && (
              <S.AlreadyAMemberWrapper>
                <AlreadyAMemberButton path={buyIdpPath} />
              </S.AlreadyAMemberWrapper>
            )}
            {!isAuthenticated && <MembershipBundle membershipProductData={membershipProductData} />}
            <S.Heading variant={TextVariant.Header3}>Personalia</S.Heading>
            <S.ControllersWrapper>
              <FirstName
                name="firstName"
                isDisabled={profile?.firstName?.editable === false || false}
                defaultValue={idpCustomerForm && idpCustomerForm.firstName}
                errorMessage={errors.firstName?.message}
                control={control}
              />
              <LastName
                name="lastName"
                isDisabled={profile?.lastName?.editable === false || false}
                defaultValue={(idpCustomerForm && idpCustomerForm.lastName) || ''}
                errorMessage={errors.lastName?.message}
                control={control}
              />
            </S.ControllersWrapper>
            <S.FullWidthFieldWrapper>
              <Email
                name="eMail"
                isDisabled={profile?.email?.editable === false || false}
                defaultValue={(idpCustomerForm && idpCustomerForm.eMail) || ''}
                control={control}
                errorMessage={errors.eMail?.type !== 'server' ? errors.eMail?.message : undefined}
              />
            </S.FullWidthFieldWrapper>
            <S.FlexWrapper>
              <PhoneNumber
                name="phoneNumber"
                defaultValue={idpCustomerForm?.phoneNumber || parsePhoneNumber('')}
                control={control}
                errorMessage={errors.phoneNumber?.type !== 'server' ? errors.phoneNumber?.message : undefined}
              />
              <S.BirthDateWrapper as="div">
                <Label>Fødselsdato</Label>
                <BirthDateDay
                  name="birthDate.day"
                  isDisabled={profile?.dateOfBirth?.editable === false || false}
                  control={control}
                  defaultValue={(idpCustomerForm && idpCustomerForm.birthDate?.day) || ''}
                  errorMessage={errors.birthDate?.day?.message}
                  clearError={() => clearErrors('birthDate')}
                />
                <BirthDateMonth
                  name="birthDate.month"
                  isDisabled={profile?.dateOfBirth?.editable === false || false}
                  control={control}
                  defaultValue={(idpCustomerForm && idpCustomerForm.birthDate?.month) || ''}
                  errorMessage={errors.birthDate?.month?.message}
                  clearError={() => clearErrors('birthDate')}
                />
                <BirthDateYear
                  name="birthDate.year"
                  isDisabled={profile?.dateOfBirth?.editable === false || false}
                  control={control}
                  defaultValue={(idpCustomerForm && idpCustomerForm.birthDate?.year) || ''}
                  errorMessage={errors.birthDate?.year?.message}
                  clearError={() => clearErrors('birthDate')}
                />
                {errors.birthDate?.type === 'server' && <Message error>{errors.birthDate?.message}</Message>}
              </S.BirthDateWrapper>
            </S.FlexWrapper>
            <S.AddressWrapper>
              <Address
                name="address"
                isDisabled={profile?.address?.editable === false || false}
                control={control}
                defaultValue={(idpCustomerForm && idpCustomerForm.address) || ''}
                errorMessage={errors.address?.message}
                handleChange={(option) => {
                  if (option.postnummer) {
                    setValue('zip', option.postnummer, { shouldDirty: true, shouldTouch: true });
                    clearErrors('zip');
                  }
                  if (option.poststed) {
                    setValue('city', option.poststed, { shouldDirty: true, shouldTouch: true });
                    clearErrors('city');
                  }
                }}
                placeholder="Gatenavn og -nummer"
              />
            </S.AddressWrapper>
            <S.ControllersWrapper>
              <ZipCode
                name="zip"
                isDisabled={profile?.postalCode?.editable === false || false}
                control={control}
                defaultValue={(idpCustomerForm && idpCustomerForm.zip) || ''}
                errorMessage={errors.zip?.message}
              />
              <City
                name="city"
                isDisabled={profile?.city?.editable === false || true}
                control={control}
                defaultValue={(idpCustomerForm && idpCustomerForm.city) || ''}
                errorMessage={errors.city?.message}
              />
            </S.ControllersWrapper>
            <S.ControllersWrapper>
              <BirthCity
                name="cityOfBirth"
                isDisabled={false}
                control={control}
                defaultValue={(idpCustomerForm && idpCustomerForm.cityOfBirth) || ''}
                errorMessage={errors.cityOfBirth?.message}
              />
              <BirthCountry
                name="countryOfBirth"
                isDisabled={false}
                control={control}
                defaultValue={(idpCustomerForm && idpCustomerForm.countryOfBirth) || ''}
                errorMessage={errors.countryOfBirth?.message}
              />
            </S.ControllersWrapper>
          </form>
        </GridCol>
        <GridCol s="12" m="12" l="4" xl="4" justify={{ s: 'stretch', m: 'stretch', l: 'end', xl: 'end' }}>
          <Summary products={internationalDrivingPermitForm?.product} />
        </GridCol>
        <GridCol s="12" m="12" l="12" xl="12">
          <WizardNavigation
            onGoback={onGoBack}
            totalSteps={totalSteps}
            currentStep={currentStep}
            nextStep={nextStep}
            previousStep={previousStep}
            isValid={isValid}
            onSubmit={onSubmit}
          />
        </GridCol>
      </GridRow>
    </FormProvider>
  );
};
